import { Avatar } from '@components/avatar';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconPerson } from '@components/icons/person';
import { StandaloneMenuItem } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';

import { EditableCellPopover, useInlineSave } from './editable-cell-popover';
import { EditableOptionMenuList } from './editable-option-menu-list';
import { editableCellLocale } from './locale';
import { EditableCellEditorProps, UserCellValue } from './types';
import { useEditOptions } from './use-edit-options';
import { useEditableCellTrigger } from './use-editable-cell-trigger';
import { useOptionSearch } from './use-option-search';

import { AvatarName } from '../renderers';
import { isEmptyColumnValue } from '../table-utils';

type EditableUserCellProps = EditableCellEditorProps<UserCellValue | undefined>;

export const EditableUserCell = ({ column, row, value }: EditableUserCellProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const getTrigger = useEditableCellTrigger();
  const { options, isLoading, hasError, load } = useEditOptions(column.getEditOptions);
  const search = useOptionSearch(options);
  const { error, save } = useInlineSave();

  const isEmpty = isEmptyColumnValue(value);
  const trigger = getTrigger(value, column, row, () => (
    <AvatarName name={value?.name || value?.email || value?.shortId || ''} size="sm" />
  ));

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      isCommitOnClick
      validationError={error}
      onOpen={() => {
        load();
        search.reset();
      }}
    >
      {(close) => (
        <EditableOptionMenuList
          options={options}
          isLoading={isLoading}
          hasError={hasError}
          load={load}
          search={search}
          alwaysShowSearch
        >
          {(filteredOptions) => (
            <>
              <StandaloneMenuItem
                label={t('noUser')}
                icon={<IconPerson size={16} />}
                isActive={isEmpty}
                iconRight={
                  isEmpty ? (
                    <IconCheckmark color="elementBlue100" data-c="editable-cell-checkmark" />
                  ) : null
                }
                onSelect={() =>
                  // The cell is already empty, so clearing it would only send a
                  // save that changes nothing.
                  isEmpty
                    ? close()
                    : save(async () => {
                        await column.onEdit?.(row, '');
                      }, close)
                }
              />
              {filteredOptions.map((option) => {
                const isActive = value?.shortId === option.value;

                return (
                  <StandaloneMenuItem
                    key={option.value}
                    label={option.label}
                    icon={
                      <Avatar name={option.label} src={option.imageSrc ?? undefined} size="sm" />
                    }
                    isActive={isActive}
                    iconRight={
                      isActive ? (
                        <IconCheckmark color="elementBlue100" data-c="editable-cell-checkmark" />
                      ) : null
                    }
                    onSelect={() =>
                      // Picking who the cell already holds is the click that
                      // closes the list, not a save.
                      isActive
                        ? close()
                        : save(async () => {
                            await column.onEdit?.(row, option.value);
                          }, close)
                    }
                  />
                );
              })}
            </>
          )}
        </EditableOptionMenuList>
      )}
    </EditableCellPopover>
  );
};
